import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Step } from 'react-joyride';
import { debugLog } from '../utils/debugLogger';

// Tour version - increment when tour content changes significantly
export const TOUR_VERSION = '1.0.8';

interface TourCompletion {
  route: string;
  version: string;
  completed: boolean;
  timestamp: number;
}

interface TourContextType {
  activeSteps: Step[];
  isRunning: boolean;
  startTour: (routeKey: string) => void;
  stopTour: () => void;
  finishTour: () => void;
  skipTour: () => void;
  /** Mark tour as completed/skipped for a route so "New here?" toast is not shown again */
  markTourDismissed: (routeKey: string) => void;
  isTourCompleted: (routeKey: string) => boolean;
  currentRoute: string | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

const STORAGE_KEY = `kairos_tours_v${TOUR_VERSION}`;
const MAX_RETRIES = 3;
const RETRY_DELAY = 300;

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [activeSteps, setActiveSteps] = useState<Step[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<string | null>(null);

  // Load completion state from localStorage
  const loadCompletionState = useCallback((): Record<string, TourCompletion> => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load tour completion state:', error);
    }
    return {};
  }, []);

  // Save completion state to localStorage
  const saveCompletionState = useCallback((state: Record<string, TourCompletion>) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save tour completion state:', error);
    }
  }, []);

  // Return steps that currently exist in the DOM
  const getAvailableSteps = useCallback((steps: Step[]): { available: Step[]; missing: string[] } => {
    const available: Step[] = [];
    const missing: string[] = [];
    
    steps.forEach(step => {
      if (typeof step.target === 'string') {
        const element = document.querySelector(step.target);
        if (element) {
          available.push(step);
        } else {
          missing.push(step.target as string);
        }
      } else {
        available.push(step);
      }
    });

    return { available, missing };
  }, []);

  // Start tour with retry logic if targets aren't ready
  const startTour = useCallback((routeKey: string, retryCount = 0) => {
    // #region agent log
    debugLog({location:'TourContext.tsx:startTour:entry',message:'startTour called',data:{routeKey,retryCount},hypothesisId:'A'});
    // #endregion
    
    console.log(`[TourContext] Starting tour for route: ${routeKey}, retry: ${retryCount}`);
    
    // Import tours dynamically to avoid circular dependencies
    import('../config/tourSteps').then(({ tours }) => {
      // #region agent log
      debugLog({location:'TourContext.tsx:startTour:imported',message:'Tour config imported',data:{routeKey,toursKeys:Object.keys(tours),hasTourConfig:!!tours[routeKey]},hypothesisId:'A'});
      // #endregion
      
      const tourConfig = tours[routeKey];
      
      if (!tourConfig) {
        console.error(`[TourContext] No tour found for route: ${routeKey}`);
        // #region agent log
        debugLog({location:'TourContext.tsx:startTour:noConfig',message:'No tour config found',data:{routeKey,availableKeys:Object.keys(tours)},hypothesisId:'A'});
        // #endregion
        return;
      }

      const steps = tourConfig.steps;
      console.log(`[TourContext] Tour config loaded, ${steps.length} steps`);

      // #region agent log
      debugLog({location:'TourContext.tsx:startTour:stepsLoaded',message:'Steps loaded',data:{routeKey,stepCount:steps.length,firstStepTarget:steps[0]?.target},hypothesisId:'A,E'});
      // #endregion

      const { available, missing } = getAvailableSteps(steps);

      if (missing.length > 0) {
        console.log(`[TourContext] Targets missing (${retryCount + 1}/${MAX_RETRIES}):`, missing);
        // #region agent log
        debugLog({location:'TourContext.tsx:startTour:targetsNotReady',message:'Targets not ready',data:{routeKey,retryCount,missingTargets:missing},hypothesisId:'B'});
        // #endregion
        
        if (available.length === 0 && retryCount < MAX_RETRIES) {
          console.log(`[TourContext] Retrying in ${RETRY_DELAY}ms...`);
          setTimeout(() => {
            startTour(routeKey, retryCount + 1);
          }, RETRY_DELAY);
          return;
        }
      }

      if (available.length === 0) {
        console.error('[TourContext] No tour targets available after retries');
        return;
      }

      if (missing.length > 0) {
        console.warn('[TourContext] Starting tour with missing targets skipped:', missing);
      }

      console.log('[TourContext] Targets ready, starting tour');
      // #region agent log
      debugLog({location:'TourContext.tsx:startTour:settingState',message:'Setting tour state',data:{routeKey,stepsCount:steps.length,stepsPreview:steps.map(s=>({target:s.target,content:typeof s.content === 'string' ? s.content.substring(0,50) : 'non-string'}))},hypothesisId:'C,E'});
      // #endregion
      
      setCurrentRoute(routeKey);
      setActiveSteps(available);
      setIsRunning(true);
      
      // #region agent log
      debugLog({location:'TourContext.tsx:startTour:stateSet',message:'Tour state set',data:{routeKey},hypothesisId:'C'});
      // #endregion
    }).catch(error => {
      console.error('[TourContext] Failed to load tour configuration:', error);
      // #region agent log
      debugLog({location:'TourContext.tsx:startTour:importError',message:'Import failed',data:{routeKey,error:error instanceof Error ? error.message : String(error)},hypothesisId:'A'});
      // #endregion
    });
  }, [getAvailableSteps]);

  // Stop tour
  const stopTour = useCallback(() => {
    setIsRunning(false);
    setActiveSteps([]);
    setCurrentRoute(null);
  }, []);

  // Finish tour and save completion
  const finishTour = useCallback(() => {
    if (currentRoute) {
      const completionState = loadCompletionState();
      completionState[currentRoute] = {
        route: currentRoute,
        version: TOUR_VERSION,
        completed: true,
        timestamp: Date.now(),
      };
      saveCompletionState(completionState);
    }
    stopTour();
  }, [currentRoute, loadCompletionState, saveCompletionState, stopTour]);

  // Skip tour (same as finish, but user-initiated)
  const skipTour = useCallback(() => {
    finishTour();
  }, [finishTour]);

  // Mark tour dismissed for a route (e.g. user clicked Skip on "New here?" toast) so it is not shown again
  const markTourDismissed = useCallback((routeKey: string) => {
    const completionState = loadCompletionState();
    completionState[routeKey] = {
      route: routeKey,
      version: TOUR_VERSION,
      completed: true,
      timestamp: Date.now(),
    };
    saveCompletionState(completionState);
  }, [loadCompletionState, saveCompletionState]);

  // Check if tour is completed for a route
  const isTourCompleted = useCallback((routeKey: string): boolean => {
    const completionState = loadCompletionState();
    const completion = completionState[routeKey];
    
    // Tour is completed only if it exists and version matches
    return completion !== undefined && 
           completion.completed && 
           completion.version === TOUR_VERSION;
  }, [loadCompletionState]);

  const value: TourContextType = {
    activeSteps,
    isRunning,
    startTour,
    stopTour,
    finishTour,
    skipTour,
    markTourDismissed,
    isTourCompleted,
    currentRoute,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
